/* ============================================================
   Reviews block — shared interactivity
   Custom element: <reviews-block>

   Two modes:
   1. STATIC mode — review cards rendered server-side by Liquid.
      JS handles filter / sort / show-all only.
   2. API mode — when data-jm-token is present, JS fetches reviews
      from Judge.me's Public API, builds cards (including photos
      and videos), then runs the same filter/sort/show-all logic.
   ============================================================ */

if (!customElements.get('reviews-block')) {
  const STAR_FILLED  = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9L12 2.5Z"/></svg>';
  const STAR_OUTLINE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9L12 2.5Z"/></svg>';
  const CHECK_ICON   = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const starsHtml = (value) => {
    const filled = Math.round(Number(value) || 0);
    let html = '<span class="rv-stars rv-stars-sm" role="img" aria-label="' + filled + ' out of 5 stars">';
    for (let i = 1; i <= 5; i++) html += i <= filled ? STAR_FILLED : STAR_OUTLINE;
    html += '</span>';
    return html;
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const VIDEO_RE = /\.(mp4|mov|webm|m4v|ogv)(\?|#|$)/i;

  // Extract videos from a Judge.me review across the various shapes
  // their API uses depending on plan / response variant:
  //   r.videos: [{ urls: { original, compact }, thumbnail }]
  //   r.pictures: [{ urls: { original } }]  where original is *.mp4 etc.
  //   r.review_videos: [{ url, thumbnail_url }]
  const extractVideos = (r) => {
    const out = [];
    const sources = [];
    if (Array.isArray(r.videos)) sources.push(r.videos);
    if (Array.isArray(r.review_videos)) sources.push(r.review_videos);
    if (Array.isArray(r.media)) sources.push(r.media.filter((m) => m && (m.type === 'video' || m.media_type === 'video')));

    sources.forEach((arr) => {
      arr.forEach((v) => {
        if (!v) return;
        const src = (v.urls && (v.urls.original || v.urls.compact || v.urls.huge))
          || v.url || v.src || v.video_url || '';
        if (!src) return;
        const poster = v.thumbnail || v.thumbnail_url || v.poster
          || (v.urls && (v.urls.thumb || v.urls.thumbnail)) || '';
        out.push({ src, poster });
      });
    });

    // Catch videos hiding in the pictures array (URL ends in a video ext)
    if (Array.isArray(r.pictures)) {
      r.pictures.forEach((p) => {
        if (!p || p.hidden) return;
        const url = (p.urls && (p.urls.original || p.urls.huge || p.urls.compact)) || p.url || '';
        if (url && VIDEO_RE.test(url)) {
          out.push({ src: url, poster: p.thumbnail_url || (p.urls && p.urls.thumb) || '' });
        }
      });
    }

    return out;
  };

  // Extract photos, skipping anything that's actually a video file.
  const extractPhotos = (r) => {
    const out = [];
    if (Array.isArray(r.pictures)) {
      r.pictures.forEach((p) => {
        if (!p || p.hidden) return;
        const orig = (p.urls && (p.urls.original || p.urls.huge)) || p.url || '';
        if (!orig || VIDEO_RE.test(orig)) return;
        const thumb = (p.urls && (p.urls.compact || p.urls.small)) || orig;
        const full = (p.urls && (p.urls.huge || p.urls.original)) || thumb;
        out.push({ thumb, full });
      });
    }
    return out;
  };

  class ReviewsBlock extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-rv-list]');
      this.distRows = this.querySelectorAll('[data-rv-dist-row]');
      this.distWrap = this.querySelector('[data-rv-dist]');
      this.sortEl = this.querySelector('[data-rv-sort]');
      this.chipsWrap = this.querySelector('[data-rv-chips]');
      this.filterLabels = this.querySelector('[data-rv-filter-labels]');

      this.initialPageSize = parseInt(this.dataset.pageSize, 10) || 4;
      this.filter = 'all';
      this.sort = this.sortEl ? this.sortEl.value : 'helpful';
      this.expanded = false;

      this.apiMode = !!this.dataset.jmProxy;

      if (this.apiMode) {
        this.fetchAndRender();
      } else {
        this.collectCards();
        this.bindControls();
        this.render();
        // Liquid already dropped 1-star cards, so the rendered card count is
        // the filtered total — sync the page-level count badge to match.
        if (this.dataset.hideOneStar === 'true') this.updateExternalCount(this.cards.length);
        this.classList.remove('rv-not-ready');
      }
    }

    /* ---------- shared helpers ---------- */

    collectCards() {
      this.cards = Array.from(this.list ? this.list.querySelectorAll('[data-rv-card]') : []);
      this.cards.forEach((c) => {
        c._rvHelpful = parseInt(c.dataset.helpful, 10) || 0;
        c._rvStars = parseInt(c.dataset.stars, 10) || 0;
        c._rvDate = parseInt(c.dataset.dateUnix, 10) || 0;
      });
    }

    bindControls() {
      this.chips = this.querySelectorAll('[data-rv-chip]');
      this.showAllBtn = this.querySelector('[data-rv-show-all]');

      this.chips.forEach((b) => {
        b.addEventListener('click', () => {
          const next = b.dataset.rvChip;
          this.setFilter(this.filter === next ? 'all' : next);
        });
      });

      this.distRows.forEach((b) => {
        b.addEventListener('click', () => {
          const s = b.dataset.rvDistRow;
          this.setFilter(this.filter === s ? 'all' : s);
        });
      });

      if (this.sortEl) {
        this.sortEl.addEventListener('change', () => {
          this.sort = this.sortEl.value;
          this.render();
        });
      }

      if (this.showAllBtn) {
        this.showAllBtn.addEventListener('click', () => {
          this.expanded = !this.expanded;
          this.render();
          // surface the show-all wrap once JS has decided it's needed
          const wrap = this.showAllBtn.parentElement;
          if (wrap && wrap.hasAttribute('hidden')) wrap.removeAttribute('hidden');
        });
      }
    }

    setFilter(f) {
      this.filter = f;
      if (this.chips) {
        this.chips.forEach((c) => c.setAttribute('data-on', c.dataset.rvChip === f ? '1' : '0'));
      }
      if (this.distWrap) this.distWrap.dataset.filter = f;
      this.distRows.forEach((r) => r.setAttribute('data-on', r.dataset.rvDistRow === f ? '1' : '0'));
      this.expanded = false;
      this.render();
    }

    render() {
      if (!this.cards) return;
      let visible = this.cards.slice();
      if (this.filter !== 'all') {
        visible = visible.filter((c) => String(c._rvStars) === this.filter);
      }
      if (this.sort === 'helpful') visible.sort((a, b) => b._rvHelpful - a._rvHelpful);
      else if (this.sort === 'recent') visible.sort((a, b) => b._rvDate - a._rvDate);
      else if (this.sort === 'highest') visible.sort((a, b) => b._rvStars - a._rvStars);
      else if (this.sort === 'lowest') visible.sort((a, b) => a._rvStars - b._rvStars);

      this.cards.forEach((c) => { c.hidden = true; });
      const frag = document.createDocumentFragment();
      const cutoff = this.expanded ? visible.length : this.initialPageSize;
      visible.slice(0, cutoff).forEach((c) => { c.hidden = false; frag.appendChild(c); });
      visible.slice(cutoff).forEach((c) => { frag.appendChild(c); });
      if (this.list) this.list.appendChild(frag);

      if (this.showAllBtn) {
        const total = visible.length;
        const wrap = this.showAllBtn.parentElement;
        if (total <= this.initialPageSize) {
          if (wrap) wrap.setAttribute('hidden', '');
        } else {
          if (wrap) wrap.removeAttribute('hidden');
          const showAllLabel = this.showAllBtn.dataset.labelShow || 'Show all reviews';
          const showLessLabel = this.showAllBtn.dataset.labelLess || 'Show fewer reviews';
          this.showAllBtn.textContent = this.expanded
            ? showLessLabel
            : showAllLabel.split('[count]').join(String(total));
        }
      }

      const empty = this.querySelector('[data-rv-empty]');
      if (empty) empty.hidden = visible.length !== 0;
    }

    /* ---------- API mode ---------- */

    async fetchAndRender() {
      const proxy = this.dataset.jmProxy;
      const shop = this.dataset.jmShop;
      const productId = this.dataset.jmProductId;
      const perPage = parseInt(this.dataset.jmPerPage, 10) || 20;

      if (!proxy || !shop || !productId) {
        this.renderApiEmpty('Missing Judge.me proxy configuration.');
        this.classList.remove('rv-not-ready');
        return;
      }

      // Proxy holds the JM token in env vars and forwards to
      // https://judge.me/api/v1/reviews?... with external_id for Shopify IDs.
      const base = proxy.replace(/\/$/, '');
      const url = base
        + '?shop_domain=' + encodeURIComponent(shop)
        + '&external_id=' + encodeURIComponent(productId)
        + '&per_page=' + perPage
        + '&page=1';

      try {
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('Judge.me API ' + res.status);
        const data = await res.json();
        let reviews = (data && Array.isArray(data.reviews)) ? data.reviews : [];

        // JM's /api/v1/reviews endpoint flags has_published_videos but does NOT
        // include the actual video URLs. Hydrate those reviews via per-review fetch.
        reviews = await this.hydrateVideos(reviews, base, shop);

        this.renderApiReviews(reviews);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[reviews-block] Judge.me fetch failed:', err);
        this.renderApiEmpty('Reviews are temporarily unavailable.');
      } finally {
        this.classList.remove('rv-not-ready');
      }
    }

    async hydrateVideos(reviews, base, shop) {
      const needsHydration = reviews.filter((r) => r && r.has_published_videos === true);
      if (!needsHydration.length) return reviews;

      let firstLogged = false;
      const hydrateOne = async (r) => {
        const u = base + '?shop_domain=' + encodeURIComponent(shop) + '&review_id=' + encodeURIComponent(r.id);
        try {
          const res = await fetch(u, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!res.ok) {
            if (!firstLogged) {
              /* eslint-disable-next-line no-console */
              console.warn('[reviews-block] per-review fetch failed for id=' + r.id, res.status);
              firstLogged = true;
            }
            return r;
          }
          const data = await res.json();
          const full = (data && data.review) ? data.review : data;

          if (!firstLogged) {
            /* eslint-disable no-console */
            console.group('[reviews-block] hydrate diagnostic — review id=' + r.id);
            console.log('top-level keys:', Object.keys(data || {}));
            console.log('full review keys:', full && typeof full === 'object' ? Object.keys(full) : 'n/a');
            console.log('full review (raw JSON):', JSON.stringify(full, null, 2));
            console.groupEnd();
            /* eslint-enable no-console */
            firstLogged = true;
          }

          if (full && typeof full === 'object') {
            if (Array.isArray(full.videos)) r.videos = full.videos;
            if (Array.isArray(full.review_videos)) r.review_videos = full.review_videos;
            if (Array.isArray(full.pictures) && full.pictures.length) r.pictures = full.pictures;
            if (Array.isArray(full.media)) r.media = full.media;
          }
        } catch (e) {
          if (!firstLogged) {
            /* eslint-disable-next-line no-console */
            console.warn('[reviews-block] per-review fetch threw:', e);
            firstLogged = true;
          }
        }
        return r;
      };

      await Promise.all(needsHydration.map(hydrateOne));
      return reviews;
    }

    renderApiEmpty(msg) {
      if (!this.list) return;
      this.list.querySelectorAll('[data-rv-skeleton]').forEach((s) => s.remove());
      const p = document.createElement('p');
      p.className = 'rv-empty';
      p.textContent = msg;
      this.list.appendChild(p);
    }

    renderApiReviews(reviews) {
      if (!this.list) return;
      // strip skeletons
      this.list.querySelectorAll('[data-rv-skeleton]').forEach((s) => s.remove());

      // Only published, non-hidden, product-scoped reviews.
      // Judge.me variants of these flags differ across plans, so check them all.
      const currentProductId = String(this.dataset.jmProductId || '');
      reviews = reviews.filter((r) => {
        if (!r) return false;
        if (r.published === false) return false;
        if (r.hidden === true) return false;
        if (r.is_published === false) return false;
        if (r.spam === true) return false;
        // Skip shop / store-level reviews — only product reviews
        const type = (r.review_type || r.type || '').toString().toLowerCase();
        if (type === 'shop' || type === 'store') return false;
        // If the review carries a product reference, it must match the current one
        const reviewProductId = String(
          r.product_external_id || r.external_id || (r.product && r.product.external_id) || ''
        );
        if (currentProductId && reviewProductId && reviewProductId !== currentProductId) {
          return false;
        }
        return true;
      });

      // Hide 1-star reviews when the section setting asks for it — they must
      // not appear in the list, filters, distribution, or summary totals.
      if (this.dataset.hideOneStar === 'true') {
        reviews = reviews.filter((r) => (parseInt(r.rating, 10) || 0) !== 1);
        this.updateSummary(reviews);
        this.updateExternalCount(reviews.length);
      }

      if (!reviews.length) {
        this.renderApiEmpty('No reviews yet — be the first to review this product.');
        return;
      }

      const showPhotos = this.dataset.showPhotos !== 'false';
      const showVideos = this.dataset.showVideos !== 'false';
      const showHelpful = this.dataset.showHelpful !== 'false';
      const showShare = this.dataset.showShare !== 'false';
      const verifiedLabel = this.dataset.verifiedLabel || 'Verified buyer';
      const helpfulLabel = this.dataset.helpfulLabel || 'Helpful';
      const shareLabel = this.dataset.shareLabel || 'Share';

      const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

      reviews.forEach((r) => {
        const rating = parseInt(r.rating, 10) || 0;
        if (dist[rating] !== undefined) dist[rating]++;
      });

      // Build cards
      const frag = document.createDocumentFragment();
      reviews.forEach((r) => {
        frag.appendChild(this.buildCard(r, {
          showPhotos, showVideos, showHelpful, showShare,
          verifiedLabel, helpfulLabel, shareLabel,
        }));
      });
      this.list.appendChild(frag);
      // (a server-rendered [data-rv-empty] already exists in the list — reuse it)

      // Build chips dynamically (only for ratings that exist)
      this.buildChips(dist, reviews.length);

      // Update distribution bars + counts if section uses metafield-based bars
      this.updateDistribution(dist, reviews.length);

      this.collectCards();
      this.bindControls();
      this.render();
    }

    buildCard(r, opts) {
      const article = document.createElement('article');
      article.className = 'rv-card';
      article.setAttribute('data-rv-card', '');

      const stars = parseInt(r.rating, 10) || 0;
      const helpful = parseInt(r.curated || r.helpful_count, 10) || 0;
      const createdAt = r.created_at || r.published_at || '';
      const createdUnix = createdAt ? Math.floor(new Date(createdAt).getTime() / 1000) : 0;

      article.dataset.stars = stars;
      article.dataset.helpful = helpful;
      article.dataset.dateUnix = createdUnix;

      const reviewer = (r.reviewer && (r.reviewer.name || r.reviewer.full_name)) || r.author || '';
      const location = (r.reviewer && r.reviewer.location) || '';
      const verified = r.verified && r.verified !== 'no-verified';

      const metaParts = [];
      if (reviewer) metaParts.push(escapeHtml(reviewer));
      if (location) metaParts.push(escapeHtml(location));
      const whenStr = formatDate(createdAt);
      if (whenStr) metaParts.push(escapeHtml(whenStr));

      const verifiedPill = verified
        ? '<span class="rv-verified-pill">' + CHECK_ICON + ' ' + escapeHtml(opts.verifiedLabel) + '</span>'
        : '';

      const titleHtml = r.title ? '<h3 class="rv-card-title">' + escapeHtml(r.title) + '</h3>' : '';
      const metaHtml = metaParts.length ? '<div class="rv-meta">' + metaParts.join(' · ') + '</div>' : '';
      const bodyHtml = r.body ? '<p class="rv-body">' + escapeHtml(r.body).replace(/\n/g, '<br>') + '</p>' : '';

      let mediaHtml = '';
      if (opts.showPhotos) {
        const photos = extractPhotos(r);
        if (photos.length) {
          mediaHtml += '<div class="rv-photos">' + photos.map((p) => (
            '<a class="rv-photo" href="' + escapeHtml(p.full) + '" target="_blank" rel="noopener noreferrer">'
              + '<img src="' + escapeHtml(p.thumb) + '" alt="Review photo" loading="lazy" width="80" height="80">'
              + '</a>'
          )).join('') + '</div>';
        }
      }
      if (opts.showVideos) {
        const videos = extractVideos(r);
        if (videos.length) {
          mediaHtml += '<div class="rv-videos">' + videos.map((v) => (
            '<video class="rv-video" controls preload="metadata" playsinline '
              + (v.poster ? 'poster="' + escapeHtml(v.poster) + '" ' : '')
              + 'src="' + escapeHtml(v.src) + '"></video>'
          )).join('') + '</div>';
        }
      }

      let actionsHtml = '';
      const actions = [];
      if (opts.showHelpful) actions.push('<button type="button" class="rv-action-btn">' + escapeHtml(opts.helpfulLabel) + ' (' + helpful + ')</button>');
      if (opts.showShare)   actions.push('<button type="button" class="rv-action-btn">' + escapeHtml(opts.shareLabel) + '</button>');
      if (actions.length) actionsHtml = '<div class="rv-actions">' + actions.join('') + '</div>';

      article.innerHTML = ''
        + '<div class="rv-card-top">'
        +   '<div class="rv-card-head">'
        +     '<div class="rv-stars-line">' + starsHtml(stars) + verifiedPill + '</div>'
        +     titleHtml
        +     metaHtml
        +   '</div>'
        + '</div>'
        + bodyHtml
        + mediaHtml
        + actionsHtml;

      return article;
    }

    buildChips(dist, total) {
      if (!this.chipsWrap) return;
      this.chipsWrap.innerHTML = '';
      const allLabel = (this.filterLabels && this.filterLabels.dataset.labelAll) || 'All';

      const mkChip = (val, text, on) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rv-chip';
        btn.setAttribute('data-rv-chip', val);
        btn.setAttribute('data-on', on ? '1' : '0');
        btn.textContent = text;
        return btn;
      };
      this.chipsWrap.appendChild(mkChip('all', allLabel, true));
      [5, 4, 3, 2, 1].forEach((star) => {
        if (dist[star] > 0) {
          this.chipsWrap.appendChild(mkChip(String(star), star + (star === 1 ? ' star' : ' stars'), false));
        }
      });
    }

    updateSummary(reviews) {
      const count = reviews.length;
      const sum = reviews.reduce((a, r) => a + (parseInt(r.rating, 10) || 0), 0);
      const avg = count ? sum / count : 0;

      const avgEl = this.querySelector('.rv-avg');
      if (avgEl) avgEl.textContent = String(Math.round(avg * 10) / 10);

      const basedEl = this.querySelector('.rv-based');
      const tpl = this.dataset.basedTemplate;
      if (basedEl && tpl) basedEl.textContent = tpl.split('[count]').join(String(count));
    }

    // Sync a page-level review-count badge (e.g. near the top-of-page star
    // rating) so it reflects the filtered total, not the raw Judge.me count.
    updateExternalCount(count) {
      const el = document.getElementById('judgemeReviewCount');
      if (!el) return;
      const txt = el.textContent || '';
      el.textContent = /\d[\d,]*/.test(txt)
        ? txt.replace(/\d[\d,]*/, String(count))
        : '(' + count + ' Reviews)';
    }

    updateDistribution(dist, total) {
      if (!this.distWrap || total <= 0) return;
      this.distWrap.querySelectorAll('[data-rv-dist-row]').forEach((row) => {
        const star = parseInt(row.dataset.rvDistRow, 10);
        const count = dist[star] || 0;
        const pct = (count / total) * 100;
        const bar = row.querySelector('.rv-dist-bar > span');
        const countEl = row.querySelector('.rv-dist-count');
        if (bar) bar.style.width = pct + '%';
        if (countEl) countEl.textContent = count;
      });
    }
  }

  customElements.define('reviews-block', ReviewsBlock);
}
